import { FormEvent, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Megaphone,
  Type,
  FileText,
  Search,
  Link2,
  UserCheck,
} from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { Campaign, Link as LinkType } from "../../../types";
import Button from "../../../components/ui/Button";
import HelpHint from "../../../components/ui/HelpHint";
import { toLocalInputValue } from "../../../utils";

const COLORS = [
  "#6366f1", // brand
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // rose
  "#8b5cf6", // violet
  "#0ea5e9", // sky
  "#ec4899", // pink
  "#475569", // slate
];

interface Props {
  open: boolean;
  initial?: Campaign | null;
  onClose: () => void;
  onSaved: (c: Campaign) => void;
}

const CampaignFormModal = ({ open, initial, onClose, onSaved }: Props) => {
  const services = useAppServices();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  // GHL tag-on-click (campaign-wide)
  const [clickTags, setClickTags] = useState<string[]>([]);
  const [clickTagInput, setClickTagInput] = useState("");

  // Link membership
  const [allLinks, setAllLinks] = useState<LinkType[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [linkSearch, setLinkSearch] = useState("");
  const [linksLoading, setLinksLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset / load when the modal opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setLinkSearch("");

    setClickTagInput("");

    if (initial) {
      setName(initial.name || "");
      setDescription(initial.description || "");
      setColor(initial.color || COLORS[0]);
      setStartsAt(toLocalInputValue(initial.starts_at));
      setEndsAt(toLocalInputValue(initial.ends_at));
      setClickTags(initial.click_tags || []);
    } else {
      setName("");
      setDescription("");
      setColor(COLORS[0]);
      setStartsAt("");
      setEndsAt("");
      setClickTags([]);
    }

    // Load links + pre-select current members (when editing).
    (async () => {
      setLinksLoading(true);
      const { response } = await services.url_tracker.get();
      setLinksLoading(false);
      if (!Array.isArray(response)) {
        setAllLinks([]);
        setSelectedIds(new Set());
        return;
      }
      setAllLinks(response);
      if (initial?._id) {
        setSelectedIds(
          new Set(
            (response as LinkType[])
              .filter((l) => l.campaign_id === initial._id)
              .map((l) => l._id),
          ),
        );
      } else {
        setSelectedIds(new Set());
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?._id]);

  const addClickTag = (raw: string) => {
    const t = raw.trim().toLowerCase();
    if (!t) return;
    setClickTags((c) => (c.includes(t) || c.length >= 20 ? c : [...c, t]));
    setClickTagInput("");
  };
  const removeClickTag = (t: string) =>
    setClickTags((c) => c.filter((x) => x !== t));

  const filteredLinks = useMemo(() => {
    const q = linkSearch.trim().toLowerCase();
    if (!q) return allLinks;
    return allLinks.filter(
      (l) =>
        l.title?.toLowerCase().includes(q) ||
        l.url?.toLowerCase().includes(q),
    );
  }, [allLinks, linkSearch]);

  const toggleLink = (id: string) =>
    setSelectedIds((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allFilteredSelected =
    filteredLinks.length > 0 &&
    filteredLinks.every((l) => selectedIds.has(l._id));
  const toggleAllFiltered = () =>
    setSelectedIds((curr) => {
      const next = new Set(curr);
      if (allFilteredSelected) {
        filteredLinks.forEach((l) => next.delete(l._id));
      } else {
        filteredLinks.forEach((l) => next.add(l._id));
      }
      return next;
    });

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const payload: any = {
      name: name.trim(),
      description: description.trim(),
      color,
      starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      click_tags: clickTags,
      link_ids: Array.from(selectedIds),
    };
    if (initial?._id) payload._id = initial._id;

    const fn = initial?._id
      ? services.campaigns.update
      : services.campaigns.create;
    const { response, error: err } = await fn({ payload });
    setSubmitting(false);
    if (err) {
      setError(err.error || err.message || "Failed to save campaign");
      return;
    }
    onSaved(response);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 backdrop-blur-sm p-4 anim-in overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden my-auto">
        <div className="relative px-6 pt-6 pb-4 border-b border-ink-100">
          <div className="flex items-center gap-3">
            <span
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${color}, ${color}cc)`,
              }}
            >
              <Megaphone size={18} className="text-white" />
            </span>
            <div>
              <h2 className="font-display text-base font-semibold text-ink-900 tracking-tight">
                {initial ? "Edit campaign" : "Create a campaign"}
              </h2>
              <p className="text-xs text-ink-500 mt-0.5">
                Group links by initiative and roll up their analytics.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="absolute top-5 right-5 text-ink-400 hover:text-ink-700 hover:bg-ink-100 w-8 h-8 rounded-lg flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto"
        >
          <div>
            <label className="block text-xs font-medium text-ink-700 mb-1.5">
              Name
            </label>
            <div className="relative">
              <Type
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
              />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Summer Sale 2026"
                required
                className="w-full pl-9 pr-3 h-10 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-700 mb-1.5">
              Description (optional)
            </label>
            <div className="relative">
              <FileText
                size={14}
                className="absolute left-3 top-3 text-ink-400"
              />
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this campaign about?"
                rows={2}
                className="w-full pl-9 pr-3 py-2 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 resize-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-700 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${
                    color === c
                      ? "border-ink-900 scale-110"
                      : "border-transparent hover:scale-105"
                  }`}
                  style={{ background: c }}
                  aria-label={c}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-700 mb-1.5">
                Starts at (optional)
              </label>
              <input
                type="datetime-local"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-700 mb-1.5">
                Ends at (optional)
              </label>
              <input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
              />
            </div>
          </div>

          {/* Campaign tag-on-click (GHL) */}
          <div>
            <label className="block text-xs font-medium text-ink-700 mb-1.5">
              <span className="inline-flex items-center gap-1.5">
                <UserCheck size={13} className="text-brand-500" />
                Tag contact on click
                <HelpHint title="Campaign tag-on-click" anchor="ghl-tag">
                  These tags are added to the contact whenever they click{" "}
                  <strong>any</strong> link in this campaign (on top of each
                  link's own tags) — handy for triggering a workflow from the
                  whole campaign. The link must carry the contact id.
                </HelpHint>
              </span>
            </label>
            <p className="text-[11px] text-ink-500 mb-2">
              Applied to the contact when they click{" "}
              <span className="font-medium">any link</span> in this campaign
              (merged with each link's own tags). Needs the link to carry{" "}
              <span className="font-mono text-ink-600">?contact_id=</span>.
            </p>
            {clickTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {clickTags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-lg bg-brand-50 text-brand-700 text-xs font-medium ring-1 ring-brand-200/60"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeClickTag(t)}
                      className="w-4 h-4 rounded flex items-center justify-center hover:bg-brand-200/60 text-brand-500"
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              type="text"
              value={clickTagInput}
              onChange={(e) => setClickTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addClickTag(clickTagInput);
                } else if (
                  e.key === "Backspace" &&
                  !clickTagInput &&
                  clickTags.length
                ) {
                  removeClickTag(clickTags[clickTags.length - 1]);
                }
              }}
              onBlur={() => addClickTag(clickTagInput)}
              placeholder="Type a tag and press Enter"
              disabled={clickTags.length >= 20}
              className="w-full px-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400 disabled:bg-ink-50"
            />
          </div>

          {/* Link membership */}
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="block text-xs font-medium text-ink-700">
                Links in this campaign
              </label>
              <span className="text-[11px] text-ink-500 tabular-nums">
                {selectedIds.size} / {allLinks.length} selected
              </span>
            </div>

            <div className="border border-ink-200 rounded-lg overflow-hidden">
              {/* Search + select-all bar */}
              <div className="flex items-center gap-2 px-2.5 py-2 border-b border-ink-100 bg-ink-50/40">
                <div className="relative flex-1">
                  <Search
                    size={12}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400"
                  />
                  <input
                    type="text"
                    value={linkSearch}
                    onChange={(e) => setLinkSearch(e.target.value)}
                    placeholder="Search links..."
                    className="w-full pl-7 pr-2 h-7 bg-white border border-ink-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                  />
                </div>
                {filteredLinks.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAllFiltered}
                    className="text-[11px] font-medium text-brand-700 hover:text-brand-900 shrink-0"
                  >
                    {allFilteredSelected ? "Clear" : "Select all"}
                  </button>
                )}
              </div>

              <div className="max-h-64 overflow-y-auto bg-white">
                {linksLoading ? (
                  <div className="text-xs text-ink-400 text-center py-6">
                    Loading links...
                  </div>
                ) : filteredLinks.length === 0 ? (
                  <div className="flex flex-col items-center gap-1 py-6 text-ink-400">
                    <Link2 size={16} />
                    <span className="text-xs">
                      {linkSearch
                        ? "No links match your search"
                        : "No links yet — create some first"}
                    </span>
                  </div>
                ) : (
                  <ul className="divide-y divide-ink-100/80">
                    {filteredLinks.map((l) => {
                      const checked = selectedIds.has(l._id);
                      const isOtherCampaign =
                        l.campaign_id &&
                        l.campaign_id !== (initial?._id || "") &&
                        !checked;
                      return (
                        <li key={l._id}>
                          <label className="flex items-start gap-2.5 px-3 py-2 hover:bg-ink-50/60 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleLink(l._id)}
                              className="mt-1 w-3.5 h-3.5 accent-brand-500"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-ink-900 truncate">
                                {l.title || "Untitled"}
                              </div>
                              <div className="text-[11px] text-ink-500 truncate font-mono">
                                {l.url}
                              </div>
                            </div>
                            {isOtherCampaign && (
                              <span
                                className="shrink-0 text-[10px] px-1.5 h-4 inline-flex items-center rounded bg-amber-50 text-amber-700 ring-1 ring-amber-200/60"
                                title="Currently in another campaign — selecting will move it."
                              >
                                In other campaign
                              </span>
                            )}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
            <p className="text-[11px] text-ink-500 mt-1.5">
              Links you uncheck will be removed from the campaign on save.
            </p>
          </div>

          {error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="gradient" loading={submitting}>
              {initial ? "Save changes" : "Create campaign"}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default CampaignFormModal;
